/* eslint-disable @typescript-eslint/no-floating-promises */
/* istanbul ignore file */
/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/core';
import { AxiosError } from 'axios';
import { enGB, fr } from 'date-fns/locale';
import Joi from 'joi';
import _includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import _isNil from 'lodash/isNil';
import _isUndefined from 'lodash/isUndefined';
import moment from 'moment';
import React, { ChangeEvent, Fragment, memo, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { MdHelpOutline } from 'react-icons/md';
import { useCurrentUserProfile } from '../../../../../app/hooks/useCurrentUserProfile';
import { ReactComponent as _IconCalendar } from '../../../../../assets/img/icon-calendar-blue.svg';
import { Candidate, CandidateStatus, EngagedCandidate } from '../../../../../firebase/firestore/documents/candidate';
import { Requisition } from '../../../../../firebase/firestore/documents/requisition';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { MESSAGING_CANDIDATE_TABS, MESSAGING_TABS } from '../../../../../routes/constants';
import colors from '../../../../../styles/colors';
import { isCandidatePage } from '../../../../../utils/candidatesUtils';
import {
  clearErrorMessage,
  errorFormat,
  FormattedError,
  getErrorMessage,
  hasErrorMessage,
} from '../../../../../utils/FormErrorUtils';
import { getTranslatedNumber, getTranslatedStringValue } from '../../../../../utils/localizedNumber';
import { getTranslatedValue } from '../../../../../utils/localizedString';
import { DateAndTimePickerCoustomCSS } from '../../../../administration/positionManagement/createJobs/CreateJobPositionStyle';
import { BusinessSelectType, PositionSelectType } from '../../../../administration/positionManagement/PositionType';
import useBusinessLocations from '../../../../administration/positionManagement/useBusinessLocations';
import { drawerCSS } from '../../../../calendar/CalendarView.Style';
import { reactDatePickerCSS } from '../../../../calendar/scheduleSidebar/ReactDatePicker.Style';
import { useMessagingViewContext } from '../../../MessagingViewContext';
import SeekerListGroupType from '../../../sidebar/filters/seekerListGroupType';
import SeekerListSortType from '../../../sidebar/filters/seekerListSortType';
import { SeekersPresenter } from '../../../sidebar/seekerList/seekersPresenter';
import useHistory from '../../candidate/history/useHistory';
import useSeekerNotes from '../../candidate/notes/useSeekerNotes';
import { SeekerContainerTabItems, useSeekerContainerContext } from '../../SeekerContainerContext';
import { SelectRequisition } from '../SelectRequisition';
import { confirmationModalHeaderCSS } from './ConfirmationModal.styles';
import { DismissConfirmationModal } from './DismissConfirmationModal';
import { hireFormContractWording } from './hireFormContractWording';
import { Business, HireFormErrorResponse, HireFormType, HireFormTypeEnum, RequestHireDetailForm } from './HireFormTypeEnum';
import { usePositionDocumentSnapshot } from './usePositionDocumentSnapshot';
import { Collections } from '../../../../../firebase/firestore/collections';
import { useDocumentSnapshot } from '../../../../../firebase/firestore/hooks/useDocumentSnapshot';
import { Position, PositionType } from '../../../../../firebase/firestore/documents/position';
import { isSalaryFixed, validateSalary } from '../../../../../utils/salaryUtils';
import { ShowSalaryRange } from '../../../../../app/components/ShowSalaryRange';

const IconCalendar = memo(_IconCalendar);
registerLocale('fr', enGB);

registerLocale('fr', fr);

export type HireRejectConfirmationModalProps = {
  isOpen: boolean;
  disabled: boolean;
  onClose: () => void;
  warningMessage: boolean;
  messageModalOpen?: () => void;
  candidateFromProps?: EngagedCandidate | Candidate;
  disableAllFields?: boolean;
  onSuccessCallback?: () => void;
};

export type HireInfo = {
  businessLocation: string;
  jobPosition: string;
  joinDate?: Date | null;
  notes: string | null;
};

export type NoteDetail = {
  salary: string | number;
  salaryUnit?: string;
  customSalaryUnit?: string;
};

enum HiringSourceType {
  'DIRECT_HIRE' = 'DIRECT_HIRE',
}

enum WorkType {
  FULL_TIME = 'fullTime',
  PART_TIME = 'partTime',
}

const DefaultHireInfo: HireInfo = {
  joinDate: null,
  jobPosition: '',
  businessLocation: '',
  notes: '',
};

const maxHoursPerWeek = 150;

export const HireRejectConfirmationModal = ({
  isOpen,
  warningMessage,
  messageModalOpen,
  disabled,
  onClose,
  candidateFromProps,
  disableAllFields,
  onSuccessCallback,
}: HireRejectConfirmationModalProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { currentUserProfile } = useCurrentUserProfile();
  const seekerNotes = useSeekerNotes();
  const businessLocations = useBusinessLocations();

  const seekerList: SeekersPresenter | undefined = useStoreState((s) => s.candidate.allCandidates);
  const { histories } = useHistory(candidateFromProps as EngagedCandidate);

  const offeredSalary = histories?.getRecentOfferMadeStatus()?.[0]?.offeredSalary;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [updatedPositionId, setUpdatedPositionId] = useState<string | undefined>(undefined);
  const isHireFormEnabled = useStoreState((s) => s.app.accounts?.configuration?.hireFormEnabled);
  const hireFormEnabledPositions = useStoreState((s) => s.app.accounts?.configuration?.hireFormEnabledPositions);
  const seasonalContractPeriod = useStoreState((s) => s.app.accounts?.configuration?.seasonalContractPeriod);
  const [radioValue, setRadioValue] = useState<string>('hired');
  const { selectedCandidate: selectedCandidateFromContext, setSelectedCandidate } = useSeekerContainerContext();
  const { selectedSeeker, setSelectedEngagedCandidate, setSelectedEngagedSeeker } = useMessagingViewContext();
  const selectedCandidate = selectedCandidateFromContext || candidateFromProps;
  const business: Business | undefined = useDocumentSnapshot<Business>(Collections.BUSINESSES, selectedCandidate?.business);
  const positionData: Position | undefined = useDocumentSnapshot<Position>(Collections.POSITIONS, selectedCandidate?.position);

  const isCountryAustralia = !business ? true : business?.country === 'Australia';
  const positionType = positionData ? positionData?.positionType : undefined;

  const isDirectHireCandidate = selectedCandidate?.hiringSource?.type === HiringSourceType.DIRECT_HIRE;

  const shouldAllowHireForm = () => {
    if (isHireFormEnabled) {
      if (updatedPositionId) {
        return _includes(hireFormEnabledPositions, updatedPositionId);
      }
      if (selectedCandidate?.newHireData) {
        return _includes(hireFormEnabledPositions, selectedCandidate?.newHireData?.positionId);
      }
      return _includes(hireFormEnabledPositions, selectedCandidate?.position);
    }
    return false;
  };

  const allowHireFrom = shouldAllowHireForm();

  const earliestAvailabilityDate = selectedCandidate?.earliestAvailabilityDate;
  const [noteContent, setNoteContent] = useState<string | null>(
    allowHireFrom ? null : `${t('messaging:actions.hire.hireDetailsDesc1')}`,
  );
  const { isOpen: isDismissModalOpen, onOpen: onDismissModalOpen, onClose: onDismissModalClose } = useDisclosure();

  const [salary, setSalary] = useState<string>('');
  const [unit, setUnit] = useState<string>('hourly');
  const [customUnit, setCustomUnit] = useState<string | null>('');
  const [disabledButton, setButtonDisabled] = useState<boolean>(true);

  const [joinDate, setJoinDate] = useState<Date | null | undefined>(
    earliestAvailabilityDate ? new Date(earliestAvailabilityDate) : undefined,
  );
  const [partTimeHours, setPartTimeHours] = useState<string>('0.00');
  const [previousData, setPreviousData] = useState<string>('');
  const [endDate, setEndDate] = useState<Date | null>();
  const [fullTime, setFullTime] = useState<string>(WorkType.FULL_TIME);
  const [activeBusiness, setActiveBusiness] = useState<Business>({});
  const [hireInfo, setHireInfo] = useState<HireInfo>(DefaultHireInfo);
  const [hireFormType, setHireFormType] = useState<HireFormType | undefined>(undefined);
  const [updatedBusinessId, setUpdatedBusinessId] = useState<string | undefined>(undefined);
  const [fromHireReject, setFromHireReject] = useState<boolean>(true);
  const [previousRequisitionID, setPreviousRequisitionID] = useState<string | undefined>(selectedCandidate?.requisitionId);
  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
  const [dismissedCandidateAfterRejection, setDismissedCandidateAfterRejection] = useState<boolean>(true);
  const [selectedRequisition, setSelectedRequisition] = useState<Requisition | undefined>(undefined);
  const [isWageError, setIsWageError] = useState<boolean>(false);

  const [isPositionSalaryExceeded, setIsPositionSalaryExceeded] = useState<boolean>(false);

  const positionDocument = usePositionDocumentSnapshot(
    updatedPositionId || selectedCandidate?.newHireData?.positionId || selectedCandidate?.position,
  );

  const [positionMinSalary, setPositionMinSalary] = useState(
    positionDocument?.proposedSalary?.minValue ? Number(positionDocument?.proposedSalary?.minValue) / 100 : 0,
  );

  const [positionMaxSalary, setPositionMaxSalary] = useState(
    positionDocument?.proposedSalary?.maxValue ? Number(positionDocument?.proposedSalary?.maxValue) / 100 : 0,
  );

  React.useEffect(() => {
    const updatedMin = positionDocument?.proposedSalary?.minValue ? Number(positionDocument?.proposedSalary?.minValue) / 100 : 0;

    const updatedMax = positionDocument?.proposedSalary?.maxValue ? Number(positionDocument?.proposedSalary?.maxValue) / 100 : 0;
    setUnit(positionDocument?.proposedSalary?.unit ?? 'hourly');
    setCustomUnit(positionDocument?.proposedSalary?.customUnit ?? '');

    setPositionMinSalary(updatedMin);
    setPositionMaxSalary(updatedMax);
  }, [positionDocument]);

  const selectedBusinessLocation = businessLocations.find((businessLocation) =>
    selectedCandidate?.newHireData?.businessId
      ? businessLocation.id === selectedCandidate.newHireData.businessId
      : businessLocation.id === positionDocument?.business,
  );
  const [businessLocation, setBusinessLocation] = useState<BusinessSelectType | null>(null);
  const [contractDetailsWordings, setContractDetailsWordings] = useState<string>(hireFormContractWording(selectedSeeker?.locale));
  const { isOpen: isOpenConformationModal, onOpen: onOpenConformationModal, onClose: onCloseConformationModal } = useDisclosure();

  const accountLocale = useStoreState((s) => s.app.user?.locale);
  const requisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);

  const hireCandidate = useStoreActions((actions) => actions.messaging.hireCandidate);
  const onRequestHireDetails = useStoreActions((actions) => actions.messaging.requestHireDetails);
  const saveOfferRejectedNote = useStoreActions((actions) => actions.account.saveOfferRejectedNote);
  const dismissCandidate = useStoreActions((actions) => actions.messaging.dismissCandidate);

  let noteDetail: NoteDetail | undefined;

  //  Retrieve full-time data form position that are non-requestion and are not located in Australia.
  React.useEffect(() => {
    if (!requisitionEnabled && !isCountryAustralia && positionData) {
      setFullTime(positionData?.fullTime ? WorkType.FULL_TIME : WorkType.PART_TIME);
    }
  }, [requisitionEnabled, isCountryAustralia, positionData]);

  /* eslint-disable no-return-assign */
  seekerNotes.presenter.positionNotes
    .filter((position) => position.positionId === selectedCandidate?.position)
    .map((notepositon) => {
      return notepositon.notes.map((n) => {
        if (n.recruiterIdInNote === currentUserProfile?.id) {
          return Array.isArray(n.note)
            ? n.note.reverse().map((note) => (note.type === 'OFFER_DETAILS' ? (noteDetail = note.additionalContextData) : ''))
            : '';
        }
        return '';
      });
    });

  /* eslint-disable no-return-assign */
  const wageSchema = Joi.object()
    .keys({
      unit:
        requisitionEnabled || isCountryAustralia
          ? Joi.string().optional()
          : Joi.string()
              .required()
              .messages({
                'string.empty': t('messaging:actions.hire.confirmationModal.unitRequired'),
              }),
      value:
        requisitionEnabled || isCountryAustralia
          ? Joi.string().allow(null).optional()
          : Joi.string()
              .required()
              .messages({
                'string.empty': t('messaging:actions.hire.confirmationModal.valueRequired'),
              }),
      customUnit: Joi.string().allow(null).optional(),
    })
    .allow(null);

  const businessSchema = Joi.object().keys({
    name: Joi.string()
      .required()
      .messages({
        'string.empty': t('messaging:actions.hire.confirmationModal.businessNameRequired'),
      }),
    country: Joi.string()
      .required()
      .messages({
        'string.empty': t('messaging:actions.hire.confirmationModal.countryRequired'),
      }),
    address: Joi.string().allow('').allow(null).optional(),
    locationId: Joi.string().allow('').allow(null).optional(),
  });

  const schema = Joi.object()
    .options({ abortEarly: false })
    .keys({
      type: Joi.string().allow(null).optional(),
      positionName: Joi.string()
        .required()
        .messages({
          'string.empty': t('messaging:actions.hire.confirmationModal.positionNameRequired'),
        }),
      note: Joi.string().allow('').allow(null).optional(),
      updatedBusinessId: Joi.string().allow(null).optional(),
      updatedPositionId: Joi.string().allow(null).optional(),
      wage: wageSchema,
      business: businessSchema,
      fullTime: Joi.boolean().allow(null).optional(),
      partTimeHours: Joi.string().optional(),
      positionType:
        isCountryAustralia && positionType
          ? Joi.string()
              .valid(...Object.values(PositionType))
              .required()
              .messages({
                'any.required': t('messaging:actions.hire.confirmationModal.positionTypeRequired'),
                'any.only': t('messaging:actions.hire.confirmationModal.invalidPositionType'),
              })
          : Joi.forbidden(),
      startingDate:
        requisitionEnabled || isCountryAustralia
          ? Joi.date().optional()
          : Joi.date()
              .required()
              .messages({
                'any.required': t('messaging:actions.hire.confirmationModal.startDateRequired'),
              }),
      endDate: Joi.date().allow(null).optional(),
      contractDetailsWordings: Joi.string().allow(null).optional(),
      requisitionId: requisitionEnabled ? Joi.string().required() : Joi.string().allow('').optional(),
    })
    .options({ stripUnknown: true });

  const formValidation = (formData: RequestHireDetailForm) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    if (requisitionEnabled) return true;
    const { error } = schema.validate(formData);
    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }
    setFormErrors([]);
    return true;
  };

  const updateNoteContent = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContent(e.target.value);
  };
  const handleCustomUnitChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCustomUnit(e.target.value);
  };
  const checkLengthOfStringSeparatedByDotOrComma = (givenString: string, stringLength: number) => {
    let allowInput = true;
    const spliterator = givenString?.includes('.') ? givenString?.split('.') : givenString?.split(',');
    if (spliterator[1]) {
      if (spliterator[1].length > stringLength) {
        allowInput = false;
      }
    }
    return allowInput;
  };
  const [salaryError, setSalaryError] = useState<string>('');

  const handleSalaryChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!checkLengthOfStringSeparatedByDotOrComma(e.target.value, 2)) return;
    const { validatedSalary, error } = validateSalary(
      e.target.value,
      positionMinSalary,
      positionMaxSalary,
      isCountryAustralia,
      t,
    );
    setSalary(validatedSalary);
    setSalaryError(error);
    setFormErrors(clearErrorMessage(formErrors, 'wage.value'));
  };
  const handlePartTimeHoursChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!checkLengthOfStringSeparatedByDotOrComma(e.target.value, 2)) {
      return;
    }
    setPartTimeHours(e.target.value);
    setFormErrors(clearErrorMessage(formErrors, 'partTimeHours'));
  };
  const handleUnitChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setUnit(e.target.value);
    if (e.target.value !== 'custom') {
      setCustomUnit(null);
    }
    setFormErrors(clearErrorMessage(formErrors, 'note'));
  };

  const submitNote = async (dismissNote?: string, dismissNoteArea?: string) => {
    onCloseConformationModal();
    if (!selectedCandidate || !currentUserProfile) return;

    try {
      setIsLoading(true);
      await saveOfferRejectedNote({
        positionId: selectedCandidate.position,
        seekerId: selectedCandidate.seeker,
        offerRejectedDetails: noteContent,
      });
      if (dismissedCandidateAfterRejection) {
        await dismissCandidate({
          positionId: selectedCandidate.position,
          seekerId: selectedCandidate.seeker,
          recruiterId: currentUserProfile.id,
          dismissalReason: dismissNote === 'DISMISSAL_REASONS_OTHER' ? dismissNoteArea : dismissNote,
        });
      }
      onSuccessCallback?.();
      onClose();
      toast({
        title: t('notes:noteSuccessMsg.title'),
        description: t('notes:noteSuccessMsg.description'),
        duration: 5000,
        isClosable: true,
      });
    } catch (e) {
      const error = e as AxiosError;
      if (error?.response?.status === 401) {
        toast({
          title: 'Error',
          description: (error.response?.data as string) ?? 'User does not have enough permission.',
          status: 'error',
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
      setFromHireReject(false);
      onClose();
    }
  };

  const messageModalOpenHandler = () => {
    if (warningMessage) {
      messageModalOpen?.();
    }
  };
  /* eslint-disable */
  const changeCandidateOnTheLeftPanelHandler = () => {
    if (!seekerList || !seekerList.engagedCandidates) return;
    const activeListLength = seekerList.getSeekersList(
      i18n.language,
      SeekerListSortType.ALPHABETICAL,
      '',
      SeekerListGroupType.NONE,
    ).length;
    // istanbul ignore else
    if (activeListLength > 0) {
      const { candidate } = seekerList.getSeekersList(
        i18n.language,
        SeekerListSortType.ALPHABETICAL,
        '',
        SeekerListGroupType.NONE,
      )[0].item;
      const { id } = seekerList.getSeekersList(
        i18n.language,
        SeekerListSortType.ALPHABETICAL,
        '',
        SeekerListGroupType.NONE,
      )[0].item.candidate;
      setSelectedCandidate(candidate);
      setSelectedEngagedCandidate(candidate);
      setSelectedEngagedSeeker(candidate && { seekerId: candidate.seeker, candidateId: candidate.id });
      if (isCandidatePage()) {
        window.history.pushState(null, '', MESSAGING_CANDIDATE_TABS(id, SeekerContainerTabItems.Messaging));
      } else {
        window.history.pushState(null, '', MESSAGING_TABS(id, SeekerContainerTabItems.Messaging));
      }
    }
  };

  /* eslint-enable */
  const onReviewHire = () => {
    hireInfo.joinDate = joinDate;
    hireInfo.jobPosition = selectedCandidate?.jobTitle ? getTranslatedValue(selectedCandidate?.jobTitle, i18n.language) : '';
    hireInfo.businessLocation = selectedCandidate?.businessName || '';
    hireInfo.notes = noteContent;
    setHireInfo({ ...hireInfo });
  };
  const handleCreateAppointmentRequest = async () => {
    onCloseConformationModal();
    if (!selectedCandidate) {
      return;
    }

    if (!joinDate && !isCountryAustralia) {
      setFormErrors([{ label: 'startingDate', message: t('messaging:actions.hire.confirmationModal.startDateRequired') }]);
      return;
    }

    let updatedSalary: string;
    if (salary.includes(',')) {
      updatedSalary = salary.toString().replace(',', '.');
    } else {
      updatedSalary = salary.toString();
    }
    try {
      setIsLoading(true);
      await hireCandidate({
        positionId: selectedCandidate.position,
        seekerId: selectedCandidate.seeker,
        hireDetails: noteContent,
        salary: isEmpty(updatedSalary) ? null : updatedSalary,
        salaryUnit: unit,
        dateOfJoining: joinDate === null ? /* istanbul ignore next */ undefined : joinDate,
        customSalaryUnit: customUnit,
        requisitionId: requisitionEnabled ? selectedRequisition?.requisitionId : '',
        updatedBusinessId: updatedBusinessId || selectedCandidate?.newHireData?.businessId,
        updatedPositionId: updatedPositionId || selectedCandidate?.newHireData?.positionId,
      });
      onSuccessCallback?.();
      onClose();
    } catch (e) {
      const error = e as AxiosError;
      if (error?.response?.status === 401) {
        toast({
          title: 'Error',
          description: (error.response?.data as string) ?? 'User does not have enough permission.',
          status: 'error',
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
      setFromHireReject(false);
      messageModalOpenHandler();
      // changeCandidateOnTheLeftPanelHandler();
    }
  };
  const onHandleRequestHireDetails = async () => {
    onCloseConformationModal();
    let updatedSalary: string;
    if (salary.includes(',')) {
      updatedSalary = salary.toString().replace(',', '.');
    } else {
      updatedSalary = salary.toString();
    }

    const requisitionId = selectedRequisition?.requisitionId;
    const noSalary = !salary || parseInt(salary || '0', 0) <= 0;

    const hireDetailsPayload = {
      type: hireFormType?.value ?? HireFormTypeEnum.NEW_HIRE,

      ...(isCountryAustralia && positionType
        ? {
            positionType,
            fullTime: positionType !== PositionType.PART_TIME,
          }
        : {
            fullTime: fullTime === WorkType.FULL_TIME,
            partTimeHours: fullTime !== WorkType.FULL_TIME ? partTimeHours : undefined,
          }),

      positionName: hireInfo.jobPosition,
      note: isEmpty(noteContent) ? null : noteContent,
      startingDate: joinDate === null ? /* istanbul ignore next */ undefined : joinDate,
      wage: noSalary
        ? null
        : {
            unit,
            value: isEmpty(updatedSalary) ? null : updatedSalary,
            customUnit: customUnit || null,
          },
      business: activeBusiness,
      updatedBusinessId: updatedBusinessId || selectedCandidate?.newHireData?.businessId,
      updatedPositionId: updatedPositionId || selectedCandidate?.newHireData?.positionId,
      requisitionId: requisitionEnabled ? requisitionId : '',
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const fieldsForSeasonalContractor = {
      endDate,
      contractDetailsWordings,
    };

    const requestHireDetailFormFields =
      hireFormType?.value !== HireFormTypeEnum.SEASONAL
        ? hireDetailsPayload
        : { ...hireDetailsPayload, ...fieldsForSeasonalContractor };

    if (formValidation(requestHireDetailFormFields)) {
      try {
        setIsLoading(true);
        const hireRequestFormResponse = await onRequestHireDetails({
          candidateId: selectedCandidate?.id ? selectedCandidate?.id : '',
          requestHireDetailFormFields,
        });
        onSuccessCallback?.();
        if (hireRequestFormResponse !== '') {
          const { title } = hireRequestFormResponse as HireFormErrorResponse;
          toast({
            title,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        } else {
          onClose();
        }
        if (!allowHireFrom) {
          changeCandidateOnTheLeftPanelHandler();
        }
      } catch (e) {
        const error = e as AxiosError;
        if (error?.response?.status === 401) {
          toast({
            title: 'Error',
            description: (error.response?.data as string) ?? 'User does not have enough permission.',
            status: 'error',
            isClosable: true,
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };
  const handleRadioValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    // istanbul ignore else
    if (e.target.value === 'rejected') {
      setNoteContent('');
    } else if (e.target.value === 'hired') {
      // istanbul ignore next
      setNoteContent(allowHireFrom ? '' : `${t('messaging:actions.hire.hireDetailsDesc1')}`);
    }
    setRadioValue(e.target.value);
  };

  const handleJoinDateChange = (date: Date) => {
    setJoinDate(date);
  };

  const CustomDatePickerInput = (props: React.HTMLProps<HTMLInputElement>, ref: React.Ref<HTMLInputElement>) => (
    <ButtonGroup
      className={disableAllFields ? 'customDatepicker disabled-datepicker' : 'customDatepicker'}
      size="md"
      isAttached
      variant="outline"
      onClick={props.onClick}
      ref={ref}
    >
      <Button mr="-px" style={{ width: '100%' }}>
        {props.value}
      </Button>
      <IconButton style={{ padding: '0.2rem' }} aria-label="calendar" icon={<IconCalendar width={20} height={20} />} />
    </ButtonGroup>
  );
  const handleJobLocationChange = (businessLocationItem: BusinessSelectType | null) => {
    /* istanbul ignore next */
    if (!_isNil(businessLocationItem)) {
      setHireInfo((prevData) => ({
        ...prevData,

        businessLocation: businessLocationItem.value,
        jobPosition: '',
      }));
      setBusinessLocation(businessLocationItem);
      setActiveBusiness({
        name: businessLocationItem?.value,
        country: businessLocationItem?.country,
        address: businessLocationItem?.completeAddress,
        locationId: businessLocationItem?.locationId,
      });
      setSelectedRequisition(undefined);
      setPreviousRequisitionID(undefined);
      if (selectedCandidate?.newHireData) {
        setUpdatedBusinessId(businessLocationItem.id);
      } else {
        setUpdatedBusinessId(businessLocationItem.id === selectedBusinessLocation?.id ? undefined : businessLocationItem.id);
      }
      setUpdatedPositionId(undefined);
      setJoinDate(earliestAvailabilityDate ? new Date(earliestAvailabilityDate) : undefined);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
    }
    setSalaryError('');
    /* istanbul ignore next */
    setFormErrors(clearErrorMessage(formErrors, 'businessName'));
  };
  const handleNullValue = (field: string, value: string | number | null) => {
    if (!_isNil(value)) {
      if (field === 'salary') {
        return (parseInt(value as string, 10) / 100).toFixed(2);
      }
      return value.toString();
    }
    switch (field) {
      case 'salary':
        if (isCountryAustralia) return '';
        return '0.00';
      case 'unit':
        return 'hourly';
      default:
        return '';
    }
  };

  const handleJobChange = (jobPositionItem: PositionSelectType | null) => {
    const UPPDATED_POSITION_MIN_SALARY = jobPositionItem?.proposedSalary?.minValue
      ? Number(jobPositionItem?.proposedSalary?.minValue) / 100
      : 0;
    const UPDATED_POSITION_MAX_SALARY = jobPositionItem?.proposedSalary?.maxValue
      ? Number(jobPositionItem?.proposedSalary?.maxValue) / 100
      : 0;

    setPositionMinSalary(UPPDATED_POSITION_MIN_SALARY);
    setPositionMaxSalary(UPDATED_POSITION_MAX_SALARY);

    setIsWageError(false);
    let givenSalary = noteDetail?.salary;
    if (_isUndefined(givenSalary)) {
      givenSalary = '';
    }
    /* istanbul ignore next */
    if (!_isNil(jobPositionItem)) {
      setHireInfo((prevData) => ({
        ...prevData,
        jobPosition: jobPositionItem.value,
      }));
      if (selectedCandidate?.newHireData) {
        setUpdatedPositionId(jobPositionItem.id);
      } else {
        setUpdatedPositionId(jobPositionItem.id === selectedCandidate?.position ? undefined : jobPositionItem.id);
      }

      if (jobPositionItem.id !== selectedCandidate?.position) {
        setSalary(
          !_isNil(jobPositionItem?.proposedSalary)
            ? handleNullValue('salary', jobPositionItem?.proposedSalary?.minValue)
            : '0.00',
        );
        setUnit(
          !_isNil(jobPositionItem?.proposedSalary) ? handleNullValue('unit', jobPositionItem?.proposedSalary?.unit) : 'hourly',
        );
        setCustomUnit(
          !_isNil(jobPositionItem?.proposedSalary)
            ? handleNullValue('customUnit', jobPositionItem?.proposedSalary?.customUnit)
            : '',
        );
      } else {
        setSalary(getTranslatedNumber(Number(givenSalary).toFixed(2).toString(), i18n.language) as any);
        setUnit(noteDetail?.salaryUnit ?? 'hourly');
        setCustomUnit(noteDetail?.customSalaryUnit ?? '');
      }
      setJoinDate(earliestAvailabilityDate ? new Date(earliestAvailabilityDate) : null);

      // Clear previous salary errors and revalidate with updated POSITION_MIN_SALARY and POSITION_MAX_SALARY
      setSalaryError('');

      const salaryValidation = validateSalary(
        getTranslatedNumber(
          Number(
            (selectedCandidate?.newHireData && jobPositionItem.id === selectedCandidate?.newHireData.positionId) ||
              jobPositionItem.id === selectedCandidate?.positionId
              ? givenSalary
              : salary,
          )
            .toFixed(2)
            .toString(),
          i18n.language,
        ) as any,
        UPPDATED_POSITION_MIN_SALARY,
        UPDATED_POSITION_MAX_SALARY,
        isCountryAustralia,
        t,
      );
      setSalary(salaryValidation.validatedSalary);
      setSalaryError(salaryValidation.error);
    }

    /* istanbul ignore next */
    setFormErrors(clearErrorMessage(formErrors, 'positionName'));
    setSelectedRequisition(undefined);
  };

  const fullTimeRadioFeild = () => (
    <Radio colorScheme="blue" value="fullTime">
      {t('messaging:actions.hire.confirmationModal.fullTime')}
    </Radio>
  );
  // eslint-disable-next-line consistent-return
  const showFullTimeField = () => {
    if (hireFormType === undefined) {
      return fullTimeRadioFeild();
    }
    if (hireFormType.value === HireFormTypeEnum.NEW_HIRE) {
      return fullTimeRadioFeild();
    }
  };

  React.useEffect(() => {
    if (!isCountryAustralia) {
      setSalaryError('');

      setSalary(Number(offeredSalary?.salary).toFixed(2).toString() || '');
      const salaryValidation = validateSalary(
        getTranslatedNumber(Number(offeredSalary?.salary).toFixed(2).toString(), i18n.language) as any,
        positionMinSalary,
        positionMaxSalary,
        isCountryAustralia,
        t,
      );
      setSalary(salaryValidation.validatedSalary);
      if (!requisitionEnabled) {
        setSalaryError(salaryValidation.error);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHireFormEnabled, offeredSalary?.salary, allowHireFrom, isCountryAustralia]);

  React.useEffect(
    () => () => {
      setIsLoading(false);
      setNoteContent('');
      setSalary(isCountryAustralia ? '' : '0');
      // setUnit('hourly');
      // setCustomUnit('');
      setJoinDate(earliestAvailabilityDate ? new Date(earliestAvailabilityDate) : undefined);
      setButtonDisabled(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [earliestAvailabilityDate, isCountryAustralia],
  );

  React.useEffect(() => {
    if (allowHireFrom) {
      onReviewHire();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHireFormEnabled, hireFormEnabledPositions]);

  React.useEffect(() => {
    let givenSalary = noteDetail?.salary;
    if (_isUndefined(givenSalary)) {
      givenSalary = '';
    }

    setSalary(givenSalary ? (getTranslatedNumber(Number(givenSalary).toFixed(2).toString(), i18n.language) as any) : '');
    setUnit(noteDetail?.salaryUnit ?? 'hourly');
    setCustomUnit(noteDetail?.customSalaryUnit ?? '');
  }, [noteDetail, i18n.language]);

  React.useEffect(() => {
    if (
      (radioValue === 'hired' && (!/^(?:\d*[.|,]\d{1,2}|\d+)$/.exec(salary) || salary === '')) ||
      (radioValue === 'hired' && (salary as any)) <= -1 ||
      (requisitionEnabled && fullTime === WorkType.PART_TIME && parseInt(partTimeHours, 10) <= 0)
    ) {
      if (salary === '' && isCountryAustralia) {
        setButtonDisabled(false);
      } else {
        setButtonDisabled(true);
      }
    } else if (radioValue === 'hired' && unit === 'custom' && customUnit === '') {
      setButtonDisabled(true);
    } else {
      setButtonDisabled(false);
    }
  }, [
    noteContent,
    unit,
    salary,
    customUnit,
    requisitionEnabled,
    radioValue,
    joinDate,
    fullTime,
    partTimeHours,
    isCountryAustralia,
  ]);

  React.useEffect(() => {
    setContractDetailsWordings(hireFormContractWording(selectedSeeker?.locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeeker, businessLocation, joinDate, endDate]);

  React.useEffect(() => {
    setNoteContent(allowHireFrom ? '' : `${t('messaging:actions.hire.hireDetailsDesc1')}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowHireFrom]);

  React.useEffect(() => {
    if (_isNil(businessLocation) && !_isNil(selectedBusinessLocation) && !_isNil(selectedBusinessLocation.id)) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      setBusinessLocation(selectedBusinessLocation);
      setActiveBusiness({
        name: selectedBusinessLocation?.value,
        country: selectedBusinessLocation?.country,
        address: selectedBusinessLocation?.completeAddress,
        locationId: selectedBusinessLocation?.locationId,
      });
    }
  }, [businessLocation, selectedBusinessLocation]);

  const onHandelRequsitionSelect = (requisition?: Requisition) => {
    setEndDate(requisition?.endDate);
    if (requisition?.seasonal) {
      setHireFormType((prevData) => ({
        ...prevData,
        value: HireFormTypeEnum.SEASONAL,
        label: t('messaging:actions.hire.confirmationModal.startSeasonalContractor'),
      }));
    } else {
      setHireFormType((prevData) => ({
        ...prevData,
        value: HireFormTypeEnum.NEW_HIRE,
        label: t('messaging:actions.hire.confirmationModal.startNewHire'),
      }));
    }
    setFullTime(requisition?.fullTime ? WorkType.FULL_TIME : WorkType.PART_TIME);
    setCustomUnit(requisition?.wage?.customUnit ? requisition?.wage?.customUnit : null);
    setUnit(offeredSalary?.salaryUnit || '');
    setSalary(Number(offeredSalary?.salary).toFixed(2).toString() || '');
    setPartTimeHours((requisition?.partTimeHours || '').toString());

    setSelectedRequisition(requisition);
  };

  const onHandleSubmitButtonWithOutHireForm = async (note?: string, noteTextArea?: string) => {
    if (radioValue === 'hired') {
      if (updatedBusinessId || updatedPositionId) {
        onOpenConformationModal();
      } else {
        await handleCreateAppointmentRequest();
      }
    } else {
      await submitNote(note, noteTextArea);
    }
  };
  const onHandleSubmitButtonWithHireForm = async (note?: string, noteTextArea?: string) => {
    if (radioValue === 'hired') {
      if (updatedBusinessId || updatedPositionId) {
        onOpenConformationModal();
      } else {
        await onHandleRequestHireDetails();
      }
    } else if (radioValue === 'rejected') {
      await submitNote(note, noteTextArea);
    }
  };

  const onShouldAllowHireFormSaveClick = (note?: string, noteTextArea?: string) => {
    if (requisitionEnabled && radioValue !== 'rejected') {
      if (joinDate) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        onHandleSubmitButtonWithHireForm(note, noteTextArea);
      } else {
        const selectorId = `#elementID_earliest_start_date`;
        const section = document.querySelector(selectorId);
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setFormErrors([{ label: 'startingDate', message: t('messaging:actions.hire.confirmationModal.startDateRequired') }]);
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      onHandleSubmitButtonWithHireForm(note, noteTextArea);
    }
  };

  const onDismissSuccess = async (note?: string, noteTextArea?: string) => {
    if (!allowHireFrom) {
      await onHandleSubmitButtonWithOutHireForm(note, noteTextArea);
    } else {
      onShouldAllowHireFormSaveClick(note, noteTextArea);
    }
  };

  const isSubmitButtonDisabled =
    requisitionEnabled && radioValue === 'hired'
      ? !selectedRequisition ||
        isEmpty(offeredSalary?.salary.toString()) ||
        isLoading ||
        isWageError ||
        (!isCountryAustralia && isEmpty(salary)) // for requisition account, wageError is added to check if salary is in within the range of requisition given wage
      : disabledButton ||
        disabled ||
        isLoading ||
        (!isCountryAustralia && isEmpty(salary)) ||
        (!isCountryAustralia && parseInt(salary, 10) < 1) ||
        (updatedBusinessId ? !updatedPositionId : false);

  const isSaveBtnDisabled =
    requisitionEnabled && radioValue === 'hired'
      ? !selectedRequisition || isEmpty(offeredSalary?.salary.toString()) || (!isCountryAustralia && isEmpty(salary))
      : disabledButton ||
        disabled ||
        isLoading ||
        (updatedBusinessId ? !updatedPositionId : false) ||
        (!isCountryAustralia && isEmpty(salary));

  const isCurrentSalaryFixedInAustralia = isSalaryFixed(positionMinSalary, positionMaxSalary) && isCountryAustralia;
  const hasValidSalaries = !isNaN(positionMaxSalary) && !isNaN(positionMinSalary);
  const invalidRange = positionMaxSalary <= positionMinSalary || positionMaxSalary === 0;

  const offeredPositionId = selectedCandidate?.newHireData?.positionId || selectedCandidate?.positionId;

  const isNonReqSubmitDisabled =
    (!isCountryAustralia && !updatedPositionId) ||
    (!isCountryAustralia && offeredPositionId === updatedPositionId) ||
    (isCurrentSalaryFixedInAustralia ? false : hasValidSalaries && invalidRange);

  return (
    <Box>
      <Modal
        id="HireRejectConfirmationModal"
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
        size={requisitionEnabled || isHireFormEnabled ? '4xl' : '3xl'}
        scrollBehavior="inside"
      >
        <ModalOverlay>
          <ModalContent>
            <ModalHeader css={confirmationModalHeaderCSS} className="modal-header" style={{ color: 'black' }}>
              {!allowHireFrom ? (
                t('messaging:actions.hire.rejectModal.header')
              ) : (
                <Box display="flex">
                  {t('messaging:actions.hire.confirmationModal.candidateHiringForm')}
                  <Text color={colors.blue.default} ml="8px">
                    {hireFormType ? `(${hireFormType.label})` : ''}
                  </Text>
                </Box>
              )}
            </ModalHeader>
            <ModalBody css={[drawerCSS, reactDatePickerCSS]}>
              {
                <Fragment>
                  {selectedCandidate?.computedStatus?.status === CandidateStatus.OfferMade && (
                    <Fragment>
                      <Text color="#455A63" mb="1rem">
                        {t('messaging:actions.hire.rejectModal.body')}
                      </Text>
                      <RadioGroup mb="2rem">
                        <Stack spacing={5} direction="row">
                          <Radio
                            isChecked
                            colorScheme="blue"
                            data-testid="Hired"
                            value="hired"
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleRadioValueChange(e)}
                          >
                            {t('messaging:actions.hire.rejectModal.hired')}
                          </Radio>
                          <Radio
                            colorScheme="blue"
                            data-testid="Rejected"
                            value="rejected"
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleRadioValueChange(e)}
                          >
                            {t('messaging:actions.hire.rejectModal.rejected')}
                          </Radio>
                        </Stack>
                      </RadioGroup>
                    </Fragment>
                  )}

                  {radioValue === 'hired' && (
                    <Fragment>
                      {!_isNil(selectedCandidate?.earliestAvailabilityDate) && (
                        /* istanbul ignore next */
                        <Box>
                          <Text fontWeight={700}>{t('common:earliestAvailabilityDate')}</Text>
                          <Text color="gray.500" fontSize="14px">
                            {moment(selectedCandidate?.earliestAvailabilityDate).locale(i18n.language).format('ll')}
                          </Text>
                        </Box>
                      )}
                      <Flex alignItems="baseline">
                        <Box
                          mt="12px"
                          data-testid="DateOfJoining"
                          w="49%"
                          mr={3}
                          css={DateAndTimePickerCoustomCSS}
                          id="elementID_earliest_start_date"
                        >
                          <FormControl isInvalid={hasErrorMessage(formErrors, 'startingDate')} isRequired={!isCountryAustralia}>
                            <FormLabel mb="8px" fontWeight="bold">
                              {t('messaging:actions.hire.dateOfJoining')}
                            </FormLabel>
                            <DatePicker
                              className="disabled-datepicker "
                              selected={joinDate}
                              customInput={React.createElement(React.forwardRef(CustomDatePickerInput))}
                              locale={i18n.language}
                              dateFormat="P"
                              shouldCloseOnSelect
                              minDate={new Date()}
                              onChange={
                                /* istanbul ignore next */ (date) => {
                                  handleJoinDateChange(date as Date);
                                  setFormErrors(clearErrorMessage(formErrors, 'startingDate'));
                                }
                              }
                              disabled={disableAllFields}
                            />
                            <FormErrorMessage>{getErrorMessage(formErrors, 'startingDate')}</FormErrorMessage>
                          </FormControl>
                        </Box>
                      </Flex>
                      <Box mt="12px">
                        <SelectRequisition
                          candidateFromProps={candidateFromProps}
                          positionId={updatedPositionId || selectedCandidate?.position || ''}
                          requisitionId={previousRequisitionID}
                          onRequisitionChanged={(requisition) => onHandelRequsitionSelect(requisition)}
                          handleJobLocationChange={(businessLocationItem: BusinessSelectType | null) =>
                            handleJobLocationChange(businessLocationItem)
                          }
                          handleJobChange={(jobPositionItem: PositionSelectType | null) => handleJobChange(jobPositionItem)}
                          isOpenConformationModal={isOpenConformationModal}
                          onHandleConformationModal={async (conformation: boolean) => {
                            if (conformation) {
                              if (radioValue === 'hired') {
                                if (allowHireFrom) await onHandleRequestHireDetails();
                                else await handleCreateAppointmentRequest();
                              } else {
                                await submitNote();
                              }
                            } else {
                              onCloseConformationModal();
                            }
                          }}
                          showIcon
                          updatedWageOfRequisition={getTranslatedStringValue(salary, i18n.language).toString()}
                          setUpdatedWageOfRequisition={setSalary}
                          setWageError={setIsWageError}
                          disableAllFields={disableAllFields || isDirectHireCandidate}
                          isPositionSalaryExceeded={isPositionSalaryExceeded}
                          setIsPositionSalaryExceeded={setIsPositionSalaryExceeded}
                        />
                      </Box>
                      {allowHireFrom ? (
                        <Fragment>
                          <Flex pt="15px" justifyContent="space-between">
                            {!requisitionEnabled && (
                              <Box mb="1rem" mr={3} data-testid="HiredDiv" flex="1">
                                <Flex>
                                  <Text mb="8px" fontWeight="bold" mr={2}>
                                    {` ${t('messaging:actions.hire.confirmationModal.salary')} ($) : `}
                                  </Text>
                                  {!isCountryAustralia && (
                                    <Box as="span" color="red.500" ml={1}>
                                      *
                                    </Box>
                                  )}
                                </Flex>
                                <Flex justifyContent="space-between" alignItems="center">
                                  <Flex>
                                    <Select
                                      onChange={(e: ChangeEvent<HTMLSelectElement>) => handleUnitChange(e)}
                                      data-testid="UnitValue"
                                      size="sm"
                                      mr={2}
                                      style={{
                                        height: '40px',
                                        background: `${colors.gray['150']}`,
                                        border: 'none',
                                      }}
                                      value={unit}
                                      width="120px"
                                      disabled={Boolean(positionDocument?.proposedSalary?.unit)}
                                    >
                                      <option value="hourly">{t('messaging:actions.hire.confirmationModal.hourly')}</option>
                                      <option value="annual">{t('messaging:actions.hire.confirmationModal.annually')}</option>
                                      <option value="custom">{t('messaging:actions.hire.confirmationModal.custom')}</option>
                                    </Select>

                                    {unit === 'custom' && (
                                      <Box>
                                        <Input
                                          mr={2}
                                          style={{
                                            height: '40px',
                                            width: '120px',
                                            background: `${colors.gray['150']}`,
                                            border: 'none',
                                          }}
                                          value={customUnit ?? ''}
                                          data-testid="CustomUnitValue"
                                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleCustomUnitChange(e)}
                                          placeholder={t('messaging:actions.hire.confirmationModal.customUnit')}
                                          size="sm"
                                          disabled={Boolean(positionDocument?.proposedSalary?.unit)}
                                        />
                                      </Box>
                                    )}

                                    <Box mr={unit === 'custom' ? 2 : 0}>
                                      <FormControl isInvalid={!!salaryError}>
                                        <Flex alignItems="center">
                                          <NumberInput
                                            flex="1"
                                            max={(i18n.language === 'en' && positionMaxSalary) || undefined}
                                            precision={2}
                                            value={getTranslatedStringValue(salary, i18n.language)}
                                            isDisabled={isNonReqSubmitDisabled}
                                          >
                                            <NumberInputField
                                              _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                                              sx={{
                                                height: '40px',
                                                background: `${colors.gray['150']}`,
                                                border: 'none',
                                                paddingLeft: '10px',
                                                paddingRight: '10px',
                                                textAlign: 'left',
                                                minWidth: '120px',
                                              }}
                                              width="160px"
                                              data-testid="SalaryValueHireForm"
                                              type="text"
                                              value={getTranslatedStringValue(salary, i18n.language)}
                                              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSalaryChange(e)}
                                              placeholder={t('messaging:actions.hire.confirmationModal.salaryValue')}
                                              onBlur={() => {
                                                const { validatedSalary, error } = validateSalary(
                                                  salary,
                                                  positionMinSalary,
                                                  positionMaxSalary,
                                                  isCountryAustralia,
                                                  t,
                                                );
                                                setSalary(validatedSalary);
                                                setSalaryError(error);
                                              }}
                                            />
                                          </NumberInput>

                                          <Text ml="6px" fontSize={14} width="max-content">
                                            <ShowSalaryRange
                                              minSalary={positionMinSalary}
                                              maxSalary={positionMaxSalary}
                                              unit={unit}
                                              customUnit={customUnit ?? ''}
                                            />
                                          </Text>
                                        </Flex>

                                        <FormErrorMessage>{salaryError}</FormErrorMessage>
                                      </FormControl>
                                    </Box>
                                  </Flex>
                                </Flex>
                              </Box>
                            )}
                          </Flex>

                          {!requisitionEnabled && (
                            <Flex pt="15px" flex="1" minH="70px" alignItems="center">
                              {isCountryAustralia && positionType && (
                                <Box alignItems="top" display="flex">
                                  <FormControl>
                                    <FormLabel mr={0} mb="8px" fontWeight="bold">
                                      {t('messaging:actions.hire.confirmationModal.positionTypeLabel')}
                                    </FormLabel>
                                    {
                                      {
                                        [PositionType.FULL_TIME]: t(`jobPosition:full_time`),
                                        [PositionType.PART_TIME]: t(`jobPosition:part_time`),
                                        [PositionType.CASUAL]: t(`jobPosition:casual`),
                                        [PositionType.SEASONAL]: t(`jobPosition:seasonal`),
                                        [PositionType.CONTRACT]: t(`jobPosition:contract`),
                                      }[positionType]
                                    }
                                  </FormControl>
                                </Box>
                              )}
                              {!isCountryAustralia && (
                                <Box alignItems="center" display="flex" height="100%">
                                  <Box
                                    pointerEvents={isDirectHireCandidate ? 'none' : 'auto'}
                                    opacity={isDirectHireCandidate ? 0.4 : 1}
                                  >
                                    <Flex alignItems="center" display="flex" data-testid="full-time-radio-group">
                                      <RadioGroup
                                        w="100%"
                                        value={fullTime}
                                        isDisabled={isDirectHireCandidate}
                                        onChange={(value: string) => setFullTime(value)}
                                      >
                                        <Stack spacing={5} direction="row">
                                          {showFullTimeField()}
                                          <Radio colorScheme="blue" value="partTime">
                                            {t('messaging:actions.hire.confirmationModal.partTime')}
                                          </Radio>
                                        </Stack>
                                      </RadioGroup>
                                    </Flex>
                                  </Box>
                                  <Box pl={4} display="flex" height="100%" alignItems="top">
                                    {fullTime !== WorkType.FULL_TIME && (
                                      <FormControl isInvalid={hasErrorMessage(formErrors, 'partTimeHours')} isRequired>
                                        <FormLabel mr={0} fontSize="xs" fontWeight="bold">
                                          {t('messaging:actions.hire.confirmationModal.partTimeHoursPerWeek')}
                                        </FormLabel>

                                        <NumberInput
                                          max={
                                            // istanbul ignore next
                                            (i18n.language === 'en' && maxHoursPerWeek) || undefined
                                          }
                                          precision={2}
                                          min={1}
                                          value={getTranslatedStringValue(partTimeHours, i18n.language)}
                                          isDisabled={isDirectHireCandidate}
                                        >
                                          <NumberInputField
                                            _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                                            style={{
                                              height: '40px',
                                              width: '150px',
                                              background: `${colors.gray['150']}`,
                                              border: 'none',
                                              paddingLeft: '10px',
                                              paddingRight: '10px',
                                              textAlign: 'left',
                                            }}
                                            data-testid="SalaryValue"
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handlePartTimeHoursChange(e)}
                                            onClick={() => {
                                              setPreviousData(partTimeHours);
                                              setPartTimeHours('');
                                            }}
                                            onBlur={() => {
                                              if (partTimeHours === '') {
                                                setPartTimeHours(previousData);
                                              }

                                              setPartTimeHours((partTimeVal) =>
                                                Number(partTimeVal.replace(',', '.')) > maxHoursPerWeek
                                                  ? maxHoursPerWeek.toString()
                                                  : Number(partTimeVal.replace(',', '.')).toFixed(2).toString(),
                                              );
                                            }}
                                            placeholder="hours/week"
                                          />
                                        </NumberInput>
                                        <FormErrorMessage>{getErrorMessage(formErrors, 'partTimeHours')}</FormErrorMessage>
                                      </FormControl>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Flex>
                          )}

                          {hireFormType && hireFormType.value === HireFormTypeEnum.SEASONAL && (
                            <Fragment>
                              <Flex>
                                {!requisitionEnabled && (
                                  <Box w="49%" mr={3}>
                                    <Box data-testid="ContactEndDate" css={DateAndTimePickerCoustomCSS}>
                                      <FormControl
                                        isInvalid={hasErrorMessage(formErrors, 'endDate')}
                                        isRequired={!isCountryAustralia}
                                      >
                                        <FormLabel my="8px" fontWeight="bold">
                                          {t('messaging:actions.hire.confirmationModal.contractEndDate')}
                                        </FormLabel>
                                        <DatePicker
                                          selected={endDate}
                                          customInput={React.createElement(React.forwardRef(CustomDatePickerInput))}
                                          locale={i18n.language}
                                          dateFormat="P"
                                          shouldCloseOnSelect
                                          minDate={joinDate}
                                          maxDate={moment(joinDate)
                                            .add(seasonalContractPeriod || 90, 'days')
                                            .toDate()}
                                          onChange={
                                            /* istanbul ignore next */ (date) => {
                                              setEndDate(date as Date);
                                              setFormErrors(clearErrorMessage(formErrors, 'endDate'));
                                            }
                                          }
                                        />
                                        <FormErrorMessage>{getErrorMessage(formErrors, 'endDate')}</FormErrorMessage>
                                      </FormControl>
                                    </Box>
                                  </Box>
                                )}
                              </Flex>

                              {!_isNil(joinDate) && !_isNil(endDate) && (
                                <Box mt={4}>
                                  <FormControl isInvalid={hasErrorMessage(formErrors, 'contractDetailsWordings')} isRequired>
                                    <Text my="8px" fontWeight="bold" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                      {t('messaging:actions.hire.confirmationModal.contractDetailsWording')}
                                      <Tooltip
                                        shouldWrapChildren
                                        label={t('messaging:actions.hire.confirmationModal.contractDetailsWordingTooltip')}
                                        fontSize="md"
                                      >
                                        <MdHelpOutline style={{ marginLeft: '5px' }} />
                                      </Tooltip>
                                    </Text>
                                    {selectedSeeker?.locale !== accountLocale && (
                                      <FormHelperText mt={0} mb={3}>
                                        {t('messaging:actions.hire.confirmationModal.contractWordingHintMessage')}
                                      </FormHelperText>
                                    )}
                                    <Textarea
                                      style={{
                                        height: '285px',
                                        width: '100%',
                                        background: `${colors.gray['150']}`,
                                        border: 'none',
                                      }}
                                      value={contractDetailsWordings}
                                      data-testid="ContractDetailsWordingContent"
                                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                                        setContractDetailsWordings(e.target.value);
                                        setFormErrors(clearErrorMessage(formErrors, 'contractDetailsWordings'));
                                      }}
                                      focusBorderColor="gray"
                                      placeholder={t(
                                        'messaging:actions.hire.confirmationModal.contractDetailsWordingPlaceholder',
                                      )}
                                    />
                                    <FormErrorMessage>{getErrorMessage(formErrors, 'contractDetailsWordings')}</FormErrorMessage>
                                  </FormControl>
                                </Box>
                              )}
                            </Fragment>
                          )}
                          <Box mt={4}>
                            <FormControl isInvalid={hasErrorMessage(formErrors, 'note')} isRequired>
                              <Text color="#455A63" mb="0.5rem" fontWeight="bold">
                                {t('messaging:actions.hire.modalMessage')}
                              </Text>
                              <Textarea
                                key="hrFormNoteContent"
                                style={{ height: '85px', width: '100%', background: `${colors.gray['150']}`, border: 'none' }}
                                value={noteContent || ''}
                                data-testid="HireNoteContent"
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                                  updateNoteContent(e);
                                  setFormErrors(clearErrorMessage(formErrors, 'note'));
                                }}
                                focusBorderColor="gray"
                                placeholder={t('messaging:actions.hire.rejectModal.seenToCandidatePlaceholder')}
                              />
                              <FormErrorMessage>{getErrorMessage(formErrors, 'note')}</FormErrorMessage>
                            </FormControl>
                          </Box>
                        </Fragment>
                      ) : (
                        <Fragment>
                          {!requisitionEnabled && (
                            <Box display="flex" mb="1rem" data-testid="HiredDiv" mt="26px">
                              <Text mb="8px" fontWeight="bold" mr={2}>
                                {` ${t('messaging:actions.hire.confirmationModal.salary')} ($) : `}
                              </Text>
                              {!isCountryAustralia && (
                                <Box as="span" color="red.500" ml={1}>
                                  *
                                </Box>
                              )}
                              <Box display="flex" flex="1" ml="12px" mt="-6px">
                                <Select
                                  onChange={(e: ChangeEvent<HTMLSelectElement>) => handleUnitChange(e)}
                                  data-testid="UnitValue"
                                  size="sm"
                                  width="150px"
                                  mr={2}
                                  style={{ height: '40px', background: `${colors.gray['150']}`, border: 'none' }}
                                  value={unit}
                                  disabled={Boolean(positionDocument?.proposedSalary?.unit)}
                                >
                                  <option value="hourly">{t('messaging:actions.hire.confirmationModal.hourly')}</option>
                                  <option value="annual">{t('messaging:actions.hire.confirmationModal.annually')}</option>
                                  <option value="custom">{t('messaging:actions.hire.confirmationModal.custom')}</option>
                                </Select>
                                {unit === 'custom' && (
                                  <Input
                                    mr={2}
                                    style={{
                                      height: '40px',
                                      width: '150px',
                                      background: `${colors.gray['150']}`,
                                      border: 'none',
                                    }}
                                    value={customUnit ?? ''}
                                    data-testid="CustomUnitValue"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleCustomUnitChange(e)}
                                    placeholder={t('messaging:actions.hire.confirmationModal.customUnit')}
                                    size="sm"
                                    disabled={Boolean(positionDocument?.proposedSalary?.unit)}
                                  />
                                )}
                                <FormControl isInvalid={!!salaryError}>
                                  <Flex alignItems="center">
                                    <Input
                                      _disabled={{ opacity: 0.8 }}
                                      style={{
                                        height: '40px',
                                        background: `${colors.gray['150']}`,
                                        border: 'none',
                                        width: '150px',
                                      }}
                                      value={getTranslatedStringValue(salary, i18n.language)}
                                      data-testid="SalaryValue"
                                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleSalaryChange(e)}
                                      placeholder={t('messaging:actions.hire.confirmationModal.salaryValue')}
                                      size="sm"
                                      disabled={isNonReqSubmitDisabled}
                                    />
                                    <Text pl="6px" mt="4px" fontSize={14} width="max-content">
                                      <ShowSalaryRange
                                        minSalary={positionMinSalary}
                                        maxSalary={positionMaxSalary}
                                        unit={unit}
                                        customUnit={customUnit ?? ''}
                                      />
                                    </Text>
                                  </Flex>
                                  <FormErrorMessage>{salaryError}</FormErrorMessage>
                                </FormControl>
                              </Box>
                            </Box>
                          )}
                          <Box mb="1rem" mt="12px">
                            <Text color="#455A63" mb="0.5rem" fontWeight="bold">
                              {t('messaging:actions.hire.modalMessage')}
                            </Text>
                            <Textarea
                              key="hireNoteContent"
                              style={{ height: '65px', width: '100%', background: `${colors.gray['150']}`, border: 'none' }}
                              value={noteContent || ''}
                              data-testid="HireNoteContent"
                              resize="none"
                              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateNoteContent(e)}
                              focusBorderColor="gray"
                              placeholder={t('messaging:actions.hire.rejectModal.seenToCandidatePlaceholder')}
                            />
                          </Box>
                        </Fragment>
                      )}
                    </Fragment>
                  )}
                  {radioValue === 'rejected' && (
                    <Box>
                      <Textarea
                        value={noteContent || ''}
                        resize="none"
                        data-testid="NoteContent"
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateNoteContent(e)}
                        focusBorderColor="gray"
                        style={{
                          height: '160px',
                          background: `${colors.gray['150']}`,
                          border: 'none',
                        }}
                        placeholder={t('messaging:actions.hire.rejectModal.placeholder')}
                      />
                      <Checkbox
                        mt={6}
                        mb={6}
                        isChecked={dismissedCandidateAfterRejection}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setDismissedCandidateAfterRejection(e.target.checked)}
                      >
                        {t('messaging:actions.dismiss.confirmationModal.header')}
                      </Checkbox>
                    </Box>
                  )}
                </Fragment>
              }
            </ModalBody>
            <ModalFooter justifyContent={selectedCandidate?.newHireData ? 'space-between' : 'flex-end'}>
              {selectedCandidate?.newHireData && (
                <Text fontStyle="italic" color={colors.orange} w="70%">
                  {t('requisition:locationAndPositionChanged')}
                </Text>
              )}
              <Flex>
                <Button data-testid="CancelButton" colorScheme="red" mr={3} onClick={() => onClose()} disabled={isLoading}>
                  {t('messaging:actions.hire.confirmationModal.cancelButton')}
                </Button>

                {!allowHireFrom ? (
                  <Button
                    data-testid="ConfirmButton"
                    colorScheme="blue"
                    onClick={async () => {
                      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                      dismissedCandidateAfterRejection && radioValue === 'rejected'
                        ? onDismissModalOpen()
                        : await onHandleSubmitButtonWithOutHireForm();
                    }}
                    disabled={isSaveBtnDisabled || !!salaryError}
                    isLoading={isLoading}
                  >
                    {t('messaging:actions.hire.confirmationModal.saveButton')}
                  </Button>
                ) : (
                  <Button
                    data-testid="submitBtn"
                    disabled={isSubmitButtonDisabled || !!salaryError || isPositionSalaryExceeded}
                    isLoading={isLoading}
                    colorScheme="blue"
                    onClick={() =>
                      dismissedCandidateAfterRejection && radioValue === 'rejected'
                        ? onDismissModalOpen()
                        : onShouldAllowHireFormSaveClick()
                    }
                  >
                    {t('messaging:actions.hire.confirmationModal.submit')}
                  </Button>
                )}
              </Flex>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>

      {isDismissModalOpen && (
        <DismissConfirmationModal
          isOpen={isDismissModalOpen}
          disabled={false}
          onClose={onDismissModalClose}
          isMessagingView
          onDismissSuccess={onDismissSuccess}
          isFromHireReject={fromHireReject}
        />
      )}
    </Box>
  );
};
